import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '@/components/layout/MainLayout/MainLayout';
import LoginPage from '@/modules/Auth/LoginPage';
import PublicRegistrationForm from '@/modules/PublicRegistration/PublicRegistrationForm';
import { useAuth } from '@/hooks/useAuth';

// Lazy load de módulos
const Dashboard = lazy(() => import('@/modules/Dashboard/Dashboard'));
const PersonasModule = lazy(() => import('@/modules/Personas/PersonasModule'));
const PersonasInteresModule = lazy(() => import('@/modules/PersonasInteres/PersonasInteresModule'));
const NegociosModule = lazy(() => import('@/modules/Negocios/NegociosModule'));
const CapacitacionesModule = lazy(() => import('@/modules/Capacitaciones/CapacitacionesModule'));
const EventosModule = lazy(() => import('@/modules/Eventos/EventosModule'));
const ApoyosModule = lazy(() => import('@/modules/Apoyos/ApoyosModule'));
const ReportesModule = lazy(() => import('@/modules/Reportes/ReportesModule'));
const AuditoriaModule = lazy(() => import('@/modules/Auditoria/AuditoriaModule'));
const UsuariosModule = lazy(() => import('@/modules/Usuarios/UsuariosModule'));
const ConfiguracionModule = lazy(() => import('@/modules/Configuracion/ConfiguracionModule'));
const CatalogosModule = lazy(() => import('@/modules/Catalogos/CatalogosModule'));

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
    <div className="spinner" />
  </div>
);

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.rol)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

export const router = createBrowserRouter([
  // Ruta pública del formulario de registro de emprendedores
  {
    path: '/public/registro',
    element: <PublicRegistrationForm />,
  },
  // Login
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  // Rutas protegidas (panel administrativo)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'personas',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PersonasModule />
          </Suspense>
        ),
      },
      {
        path: 'personas-interes',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PersonasInteresModule />
          </Suspense>
        ),
      },
      {
        path: 'negocios',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NegociosModule />
          </Suspense>
        ),
      },
      {
        path: 'capacitaciones',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <CapacitacionesModule />
          </Suspense>
        ),
      },
      {
        path: 'eventos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EventosModule />
          </Suspense>
        ),
      },
      {
        path: 'apoyos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ApoyosModule />
          </Suspense>
        ),
      },
      {
        path: 'reportes',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ReportesModule />
          </Suspense>
        ),
      },
      {
        path: 'auditoria',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingFallback />}>
              <AuditoriaModule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingFallback />}>
              <UsuariosModule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'configuracion',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ConfiguracionModule />
          </Suspense>
        ),
      },
      {
        path: 'catalogos',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <Suspense fallback={<LoadingFallback />}>
              <CatalogosModule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
]);
